import { useState } from "react";
import { useRecordContext } from "ra-core";
import { Timeline } from "../timeline/Timeline";
import { SmsThread } from "../sms/SmsThread";
import { CallLog } from "../calls/CallLog";
import { ContactEstimateList } from "../estimates/ContactEstimateList";
import { ContactJobList } from "../jobs/ContactJobList";
import { ContactInvoiceList } from "../invoices/ContactInvoiceList";
import {
  Activity,
  MessageSquare,
  Phone,
  FileText,
  Briefcase,
  DollarSign,
} from "lucide-react";

type TabId = "timeline" | "sms" | "calls" | "estimates" | "jobs" | "invoices";

interface Tab {
  id: TabId;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
}

const TABS: Tab[] = [
  { id: "timeline", label: "Timeline", icon: Activity },
  { id: "sms", label: "SMS", icon: MessageSquare },
  { id: "calls", label: "Calls", icon: Phone },
  { id: "estimates", label: "Estimates", icon: FileText },
  { id: "jobs", label: "Jobs", icon: Briefcase },
  { id: "invoices", label: "Invoices", icon: DollarSign },
];

/**
 * Tab panel for the contact detail page.
 * Reads contact ID from RecordContext (provided by ShowBase).
 */
export function ContactShowTabs() {
  const record = useRecordContext();
  const [activeTab, setActiveTab] = useState<TabId>("timeline");
  const contactId = record?.id as string | undefined;

  return (
    <div style={{ marginTop: 24 }}>
      <div
        style={{
          display: "flex",
          gap: 0,
          borderBottom: "2px solid #e5e7eb",
          marginBottom: 16,
          overflowX: "auto",
        }}
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                border: "none",
                borderBottom: isActive
                  ? "2px solid #2563eb"
                  : "2px solid transparent",
                marginBottom: -2,
                background: "none",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? "#2563eb" : "#6b7280",
                transition: "all 0.15s",
                whiteSpace: "nowrap",
              }}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>
      {activeTab === "timeline" && <Timeline contactId={contactId} />}
      {activeTab === "sms" && <SmsThread contactId={contactId} />}
      {activeTab === "calls" && <CallLog contactId={contactId} />}
      {activeTab === "estimates" && (
        <ContactEstimateList contactId={contactId} />
      )}
      {activeTab === "jobs" && <ContactJobList contactId={contactId} />}
      {activeTab === "invoices" && (
        <ContactInvoiceList contactId={contactId} />
      )}
    </div>
  );
}
